// src/Payroll_Management/PayRoll_Report/EmployeePerformanceReport.jsx

import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  ArrowLeft,
  Printer,
  RefreshCcw,
  Eye,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const EmployeePerformanceReport = () => {
  const navigate = useNavigate();
  const q = useQuery();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const empId = q.get("empId") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-30/Performance", []);

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchList = async (p = page) => {
    if (!finYearId || !empId) {
      toast.error("Employee ID or Financial Year missing.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(
        `${BASE_URL}/api/employee-report/performance`,
        {
          params: {
            finYearId: Number(finYearId),
            empId,
            page: p,
            pageSize,
          },
        }
      );

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load performance.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line
  }, [page]);

  const handleBack = () => {
    navigate("/PayRoll/EmployeeReport");
  };

const handlePrint = () => {
  // open print page (listing only)
  window.open(
    `/PayRoll/EmployeePerformanceReportPrint?empId=${encodeURIComponent(empId)}&autoPrint=1`,
    "_blank",
    "width=1200,height=800,scrollbars=yes,resizable=yes"
  );
};


  const handleView = (performId) => {
    navigate(`/PayRoll/PerformanceReportView?performId=${performId}`);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE PERFORMANCE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top Section */}
          <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
            <div>
              <div className="text-2xl font-extrabold">
                Employee Performance Listing
              </div>
              <div className="text-sm opacity-70">
                Employee ID: <b>{empId}</b>
              </div>
            </div>

            <div className="flex gap-3 flex-wrap">
              <button onClick={handleBack} className={ghostBtn}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={() => fetchList(page)} className={ghostBtn}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={primaryBtn}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center">S.No</th>
                    <th className="px-4 py-3 text-center">Employee Code</th>
                    <th className="px-4 py-3 text-center">Employee Name</th>
                    <th className="px-4 py-3 text-center">Date</th>
                    <th className="px-4 py-3 text-center">Title</th>
                    <th className="px-4 py-3 text-center">Performance</th>
                    <th className="px-4 py-3 text-center">View</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10">
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={r.fld_Perform_ID} className="border-b">
                        <td className="text-center py-3">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="text-center py-3">
                          {r.fld_EmployeeCode}
                        </td>
                        <td className="text-center py-3">
                          {r.fld_Employee}
                        </td>
                        <td className="text-center py-3">
                          {r.PerformanceDate}
                        </td>
                        <td className="text-center py-3">
                          {r.fld_title}
                        </td>
                        <td className="text-center py-3">
                          {r.fld_Details}
                        </td>
                        <td className="text-center py-3">
                          <button
                            onClick={() => handleView(r.fld_Perform_ID)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex items-center gap-2 mx-auto"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6 flex-wrap gap-3">
            <div>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of{" "}
              <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border rounded-xl"
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 rounded-xl ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "border"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border rounded-xl"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeePerformanceReport;
